import React, { useState } from 'react';
import { Button } from './ui/Button';
import { AlertTriangle, Info, CheckCircle, RotateCcw } from 'lucide-react';
const questions = ['Have you bet more than you could really afford to lose?', 'Have you needed to gamble with larger amounts of money to get the same feeling of excitement?', 'When you gambled, did you go back another day to try to win back the money you lost?', 'Have you borrowed money or sold anything to get money to gamble?', 'Have you felt that you might have a problem with gambling?', 'Has gambling caused you any health problems, including stress or anxiety?', 'Have people criticized your betting or told you that you had a gambling problem, regardless of whether or not you thought it was true?', 'Has your gambling caused any financial problems for you or your household?', 'Have you felt guilty about the way you gamble or what happens when you gamble?'];
const options = [{
  label: 'Never',
  value: 0
}, {
  label: 'Sometimes',
  value: 1
}, {
  label: 'Often',
  value: 2
}, {
  label: 'Almost always',
  value: 3
}];
export function PGSITestSection() {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResult, setShowResult] = useState(false);
  const handleOptionSelect = (questionIndex: number, value: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
  };
  const isComplete = Object.keys(answers).length === questions.length;
  const calculateScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  };
  const getResult = (score: number) => {
    if (score === 0) {
      return {
        title: 'No signs of problem gambling',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: 'Your gambling behavior shows no signs of risk. Continue to play responsibly.'
      };
    } else if (score >= 1 && score <= 2) {
      return {
        title: 'Low risk level',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        description: 'You have a low level of risk. Be mindful of your habits and control the time and money spent on gambling.'
      };
    } else if (score >= 3 && score <= 7) {
      return {
        title: 'Moderate risk',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        description: 'Your gambling may cause problems. We recommend reconsidering your attitude towards gambling and setting stricter limits.'
      };
    } else {
      return {
        title: 'High risk of problem gambling',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        description: 'Your gambling behavior carries a high risk. We strongly recommend seeking professional help from a psychologist or specialized support centers.'
      };
    }
  };
  const resetTest = () => {
    setAnswers({});
    setShowResult(false);
  };
  return <section id="pgsi-test" className="py-20 bg-white" data-id="element-665">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-666">
        <div className="text-center mb-12" data-id="element-667">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#0A5E5C] mb-6" data-id="element-668">
            Self-Assessment Tests for Gambling Addiction Risk
          </h2>
          <div className="h-1 w-24 bg-[#D4A843] mx-auto rounded-full mb-8" data-id="element-669" />
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl mb-10" data-id="element-670">
          <div className="flex items-start gap-4" data-id="element-671">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" data-id="element-672" />
            <div data-id="element-673">
              <h4 className="font-bold text-yellow-800 mb-2" data-id="element-674">Important</h4>
              <p className="text-yellow-700 text-sm leading-relaxed" data-id="element-675">
                The tests below are only tools for self-diagnosis. They are not
                a medical diagnosis and cannot replace a consultation with a
                psychologist, psychotherapist, or other qualified specialist. If
                the test results cause concern or you feel that gambling is
                starting to affect your life, it is recommended to seek
                professional help.
              </p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 mb-12" data-id="element-676">
          <div className="flex items-start gap-4" data-id="element-677">
            <Info className="w-6 h-6 text-[#0A5E5C] flex-shrink-0 mt-1" data-id="element-678" />
            <div data-id="element-679">
              <h3 className="font-serif font-bold text-xl text-[#0A5E5C] mb-3" data-id="element-680">
                1. Problem Gambling Severity Index (PGSI)
              </h3>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-681">
                PGSI is an internationally recognized tool for assessing the
                risk of problem gambling behavior.
              </p>
              <ul className="text-sm text-gray-500 space-y-1" data-id="element-682">
                <li data-id="element-683">
                  <span className="font-medium text-gray-700" data-id="element-684">
                    Developed by:
                  </span>{' '}
                  Julie Ferris and Harold Wynne (2001)
                </li>
                <li data-id="element-685">
                  <span className="font-medium text-gray-700" data-id="element-686">As part of:</span>{' '}
                  Canadian Problem Gambling Index (CPGI)
                </li>
                <li data-id="element-687">
                  <span className="font-medium text-gray-700" data-id="element-688">
                    Commissioned by:
                  </span>{' '}
                  Canadian Centre on Substance Abuse
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quiz Area */}
        {!showResult ? <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-10" data-id="element-689">
            <div className="mb-8" data-id="element-690">
              <h4 className="text-lg font-bold text-[#0A5E5C] mb-2" data-id="element-691">
                Instructions:
              </h4>
              <p className="text-gray-600" data-id="element-692">
                Answer each question by assessing your behavior over the past{' '}
                <span className="font-bold" data-id="element-693">12 months</span>.
              </p>
            </div>

            <div className="space-y-10" data-id="element-694">
              {questions.map((question, qIndex) => <div key={qIndex} className="border-b border-gray-100 pb-8 last:border-0 last:pb-0" data-id="element-695">
                  <h5 className="text-lg font-medium text-gray-800 mb-4" data-id="element-696">
                    <span className="text-[#D4A843] font-bold mr-2" data-id="element-697">
                      {qIndex + 1}.
                    </span>
                    {question}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-698">
                    {options.map(option => <button key={option.value} onClick={() => handleOptionSelect(qIndex, option.value)} className={`py-3 px-4 rounded-lg border text-sm font-medium transition-all duration-200 ${answers[qIndex] === option.value ? 'bg-[#0A5E5C] border-[#0A5E5C] text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-[#0A5E5C] hover:text-[#0A5E5C]'}`} data-id="element-699">
                        {option.label}
                      </button>)}
                  </div>
                </div>)}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-100 flex justify-center" data-id="element-700">
              <Button variant="primary" size="lg" disabled={!isComplete} onClick={() => setShowResult(true)} className="min-w-[200px]" data-id="element-701">
                See Results
              </Button>
            </div>

            {!isComplete && <p className="text-center text-sm text-gray-400 mt-4" data-id="element-702">
                Answer all questions to see your result (
                {Object.keys(answers).length} of {questions.length})
              </p>}
          </div> /* Results Area */ : <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 text-center animate-fade-in" data-id="element-703">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-704">
              <CheckCircle className="w-10 h-10 text-[#0A5E5C]" data-id="element-705" />
            </div>

            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-2" data-id="element-706">
              Your result:{' '}
              <span className="text-3xl text-[#0A5E5C]" data-id="element-707">
                {calculateScore()}
              </span>{' '}
              points
            </h3>

            <div className={`mt-8 p-6 rounded-xl border ${getResult(calculateScore()).bgColor} ${getResult(calculateScore()).borderColor}`} data-id="element-708">
              <h4 className={`text-2xl font-bold mb-4 ${getResult(calculateScore()).color}`} data-id="element-709">
                {getResult(calculateScore()).title}
              </h4>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-710">
                {getResult(calculateScore()).description}
              </p>
            </div>

            <div className="mt-10 flex justify-center" data-id="element-711">
              <Button variant="outline" onClick={resetTest} className="flex items-center gap-2" data-id="element-712">
                <RotateCcw className="w-4 h-4" data-id="element-713" />
                Take the test again
              </Button>
            </div>
          </div>}
      </div>
    </section>;
}